import { isGranted } from 'common/helpers.ts';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { Package } from 'types/Package.ts';
import type { Shipment } from 'types/Shipment.ts';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { Alerts } from './Alerts.tsx';
import { Measurement } from './Measurement.tsx';

type Props = {
  shipment: Shipment;
  pkg: Package;
  onClose?: () => void;
  isOpen: boolean;
};

export const PackageDetail: React.FC<Props> = ({ shipment, pkg, onClose, isOpen }) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const env = useEnv();

  return (
    <Modal title={<h4>{t('package.details')}</h4>} key={pkg.id} isOpen={isOpen} onClose={onClose}>
      <div className="flex max-h-[600px] w-[900px] flex-col gap-8 overflow-scroll">
        <section>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <span className="font-bold">{t('package.orderNumber')}</span>
              <CopyableText copyText={shipment.logistics_ex_id}>#{shipment.logistics_ex_id}</CopyableText>
            </div>
            <div className="flex gap-2">
              <span className="font-bold">{t('package.packageId')}</span>
              <CopyableText copyText={pkg.logistics_ex_id}>#{pkg.logistics_ex_id}</CopyableText>
            </div>
            <div className="flex gap-2">
              <span className="truncate font-bold">{t('views.shipment.labels.description')}</span>
              {pkg.description && <CopyableText copyText={pkg.description}>{pkg.description}</CopyableText>}
              {!pkg.description && '-'}
            </div>
            <Measurement value={pkg.width} unit={pkg.width_unit} translationKey="package.width" />
            <Measurement value={pkg.height} unit={pkg.height_unit} translationKey="package.height" />
            <Measurement value={pkg.length} unit={pkg.length_unit} translationKey="package.length" />
            <Measurement value={pkg.weight} unit={pkg.weight_unit} translationKey="package.weight" />
            <Measurement value={pkg.volume} unit={pkg.volume_unit} translationKey="package.volume" />
            {pkg.th_min_temperature != null && pkg.th_max_temperature != null && (
              <div className="flex gap-2">
                <span className="font-bold">{t('package.tempRange')}</span>
                <span>
                  {pkg.th_min_temperature}ºC / {pkg.th_max_temperature}ºC
                </span>
              </div>
            )}
          </div>
        </section>
        {env && shipment && isGranted(me, 'ViewAlerts', env.SUPERADMIN_GROUP_ID) && (
          <section className="flex flex-col gap-4">
            <h4>{t('package.alerts')}</h4>
            <Alerts
              hideSearch={true}
              showColumns={[t('table.columns.trackerId'), t('table.columns.datetime'), t('table.columns.alert'), t('table.columns.value'), t('table.columns.actions')]}
              shipmentId={shipment.id}
              className="!bg-blue-50"
            />
          </section>
        )}
      </div>
    </Modal>
  );
};
